import { useEffect, useState } from 'react'
import store from 'state/store'
import { AdminRbacSettings, RbacSettingList } from 'gen/rtk-openapi'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { AppTab } from 'AppRoutes'
import { AppAction } from 'utils/ui/actions'

interface UseRbacResponse {
  rbacMap: Record<string, boolean>
  isRouteAuthorized: (routePath: string) => boolean
  getAuthorizedTabs: (tabs: AppTab[]) => AppTab[]
  getAuthorizedActions: (tabs: AppAction[]) => AppAction[]
}

/**
 * Custom hook to manage role-based access control (RBAC) for routes.
 * Returns both the `rbacMap` and a utility function `isRouteAuthorized(routePath)`.
 */
const useRbac = (): UseRbacResponse => {
  const [rbacMap, setRbacMap] = useState<Record<string, boolean>>({})
  const { roles } = useAppSelector(state => state.permissions)
  const settingsData = store.getState().settings
  const rbacSettings = settingsData?.settings?.adminSettings?.rbac
  const userRole = roles[0]?.roleName

  useEffect(() => {
    const routeAccessMap: Record<string, boolean> = {}

    if (rbacSettings) {
      const roleBasedPermissions: RbacSettingList = rbacSettings[userRole]
      // push permissions settings into a map
      if (roleBasedPermissions) {
        roleBasedPermissions.forEach(rootModulePermission => {
          // Set module-level access
          routeAccessMap[rootModulePermission.route] = rootModulePermission.allow
          // Set access for child routes
          rootModulePermission?.children?.forEach(childRoute => {
            routeAccessMap[childRoute.route] = childRoute.allow
          })
        })
      }
      setRbacMap(routeAccessMap)
    }
  }, [rbacSettings, userRole])

  /**
   * Function to check if a given route is authorized based on RBAC.
   */
  const isRouteAuthorized = (routePath: string) => {
    const settingsData = store.getState().settings
    const rbacSettings: AdminRbacSettings = settingsData?.settings?.adminSettings?.rbac
    const rbacForUser: RbacSettingList = rbacSettings?.[userRole]
    const pathPart01 = routePath.split('/')[0]
    const pathPart02 = routePath.split('/')[1]
    if (rbacForUser) {
      const rootModuleRbac = rbacForUser.find(rbac => rbac.route === pathPart01)
      if (rootModuleRbac && !rootModuleRbac.allow) {
        return false
      }
      if (pathPart01 && pathPart02) {
        // for child routes we greedily match the route path i.e. if purchases/bills is denied then purchases/bills/new also is denied
        const childModuleRbac = rootModuleRbac?.children?.find(rbac => routePath.includes(rbac.route))
        return childModuleRbac != undefined ? childModuleRbac.allow : true
      }
    }
    return true
  }

  const getAuthorizedTabs = (tabs: AppTab[]) => {
    return tabs.filter(tab => {
      if (tab.matchPath) {
        const pathParts = tab.matchPath.split('/')
        const normalizedPath = pathParts.slice(2, 4).join('/')
        return isRouteAuthorized(normalizedPath)
      }
      return true
    })
  }

  const getAuthorizedActions = (actions: AppAction[]) => {
    return actions.filter(action => {
      if (!action.actionPath) return true
      return isRouteAuthorized(action.actionPath)
    })
  }

  return { rbacMap, isRouteAuthorized, getAuthorizedTabs, getAuthorizedActions }
}

export default useRbac
